/* eslint-disable no-restricted-syntax */
/* eslint-disable no-plusplus */
import lod_ from "lodash";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import InformationCard from "pages/settings/charts/steps/components/InformationCard";
import { useEffect, useState } from "react";
import ChartsActions from "redux-react/actions/chartsActions";
import { useDispatch, useSelector } from "react-redux";
import { getLabelPath } from "helpers/dictionary";
import { downloadCSV } from "helpers/csv";
import { t } from "i18next";
import { display, hide } from "redux-react/reducers/snackBarReducer";

const {
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	CircularProgress
} = require("@mui/material");

/**
 * Create headers recursively for a list
 * Handle level and array
 * @param {object} baseDictionary - Full dictionary of the list
 * @param {object} dictionary - Dictionary at the current level (items)
 * @param {Array<object>} headers - Headers to be created
 * @param {object} user - User profile
 * @param {string|null} path - Path to the current level (used for recursive calls)
 * @returns {void}
 */
const createHeadersRecursivly = (baseDictionary, dictionary, headers, user, path = null) => {
	for (let key of Object.keys(dictionary)) {
		let item = dictionary[key];
		if (item.type === "level" && !item.isArray) {
			let newPath = path ? `${path}.${item.code}` : item.code;
			createHeadersRecursivly(baseDictionary, item.items, headers, user, newPath);
		} else if (item.active) {
			let currentPath = path ? `${path}.${item.code}` : item.code;
			headers.push({
				key: currentPath,
				label: getLabelPath(baseDictionary, currentPath, "None", user.language)
			});
		}
	}
};

/**
 * Parse data to CSV format
 * @param {object\Array<object>} data - Data to be parsed
 * @param {object} dictionary - Dictionary of the list
 * @param {object} user - User profile
 * @returns {object} - Parsed data and headers
 */
function parseCSV(data, dictionary = {}, user) {
	if (data.datasets) {
		/**
		 * When chart's dataset
		 */
		let dataset = data.datasets[0];
		let res = [];
		for (let i = 0; i < dataset.data.length; i++) {
			res.push({
				label: data.labels[i],
				value: dataset.data[i]
			});
		}

		return {
			data: res,
			headers: ["Label", "Valeur"]
		};
	} else if (data.rows && data.columns) {
		/**
		 * When chart's list
		 */
		if (data.totalRow) {
			data.rows.push(data.totalRow);
		}
		let res = [];
		let headers = [];
		data.columns.map((item, colIndex) => {
			headers.push(item.Header);

			data.rows.map((row, index) => {
				let exist = true;
				let obj = res.find((r, i) => i === index);

				if (!obj) {
					obj = {};
					exist = false;
				}
				let value = row[item.accessor] ?? 0;
				obj[item.accessor] = value;

				if (!exist) {
					res.push(obj);
				}
			});
		});

		return {
			data: res,
			headers
		};
	} else {
		if (lod_.isEmpty(dictionary)) {
			return {
				error: t("components.custom.listOptions.downloadList.noDictionary")
			};
		}
		/**
		 * List using dictionary
		 */
		try {
			let headers = [];

			// Create header (column) from dictionary (only active items)
			createHeadersRecursivly(dictionary.items, dictionary?.items ?? {}, headers, user);
			// Based on the header, create datas
			data = data.map(dataItem => {
				let tempObj = {};
				for (let item of headers) {
					let value = lod_.get(dataItem, item.key) ?? "";

					if (lod_.isArray(value) || lod_.isObject(value)) {
						value = JSON.stringify(value);
					}

					tempObj[item.label] = value;
				}
				return tempObj;
			});

			return {
				data,
				headers: headers.map(h => h.label)
			};
		} catch (e) {
			return {
				data: [],
				headers: []
			};
		}
	}
}

const getUserLabel = (profile, label) => {
	let trad = label[profile.language];
	if (!trad) {
		let firstLabel = Object.keys(label)[0];
		trad = label[firstLabel];
	}
	return trad;
};

const DownloadList = ({ open, onClose: onCloseProps, chart, data, title }) => {
	const dispatch = useDispatch();
	const profile = useSelector(state => state.profile);
	const user = useSelector(state => state.user);

	const [openDialog, setOpenDialog] = useState(true);

	const [selection, setSelection] = useState("screen");
	const [totalCount, setTotalCount] = useState(0);
	const [fileName, setFileName] = useState(title);
	const [loading, setLoading] = useState(true);

	let actualListLength = data.length;

	let dictionary = chart.dictionary ?? {};

	const dictionaryFormat = chart?.resource?.format?.dictionary ?? chart?.request?.collection;
	let possibleCollection = dictionary[dictionaryFormat];

	const getCompleteList = async () => {
		return new Promise((resolve, reject) => {
			dispatch(
				ChartsActions.getListByCode(
					profile.assistantID,
					chart.code,
					chart.isSystem,
					res => {
						resolve(res.paginedList?.data?.rows ?? []);
					},
					err => {
						resolve([]); // In case of error, we resolve with an empty array to avoid blocking the download. The error will be displayed in the snack bar.
					}
				)
			);
		});
	};

	const onClose = () => {
		setOpenDialog(false);
		onCloseProps();
	};

	const downloadSelection = async () => {
		let currentData = [];

		switch (selection) {
			case "screen":
				currentData = data;
				break;
			case "all":
				currentData = await getCompleteList();
				break;
			default:
				break;
		}
		let csv = parseCSV(currentData, possibleCollection, user);

		if (!lod_.isEmpty(csv.error) || lod_.isEmpty(currentData)) {
			onClose();
			dispatch(
				display({
					type: "error",
					message: csv.error ?? t("components.custom.listOptions.downloadList.noData"),
					validation: (
						<MDButton
							color="error"
							variant="outlined"
							sx={{ ml: 1 }}
							onClick={() => {
								dispatch(hide());
							}}
						>
							{t("SETTINGS.close")}
						</MDButton>
					)
				})
			);
			return;
		}

		await downloadCSV(
			csv.data,
			{
				headerFields: csv.headers,
				delimiter: {
					field: ";"
				}
			},
			fileName + ".csv"
		);

		onClose();
	};

	useEffect(() => {
		if (open) {
			if (chart.type === "paginedList") {
				if (possibleCollection) {
					setFileName(getUserLabel(profile, possibleCollection.label));
				}

				dispatch(
					ChartsActions.getListCount(
						profile.assistantID,
						chart.request.collection,
						chart.filters,
						res => {
							setTotalCount(res.count);
							setLoading(false);
						},
						err => {
							setTotalCount(0);
							setLoading(false);
							dispatch(
								display({
									type: "error",
									message: t("components.custom.listOptions.downloadList.countError"),
									validation: (
										<MDButton
											color="error"
											variant="outlined"
											sx={{ ml: 1 }}
											onClick={() => {
												dispatch(hide());
											}}
										>
											{t("SETTINGS.close")}
										</MDButton>
									)
								})
							);
						}
					)
				);
			} else {
				onCloseProps();
				setSelection("screen");
				data.map(el => {
					delete el.color;
					delete el.label;
					if (el.name === "null_attribute") {
						delete el.name;
					}
					return el;
				});
				downloadSelection();
			}
		}

		return () => {
			setTotalCount(0);
			setSelection(null);
			setFileName("");
			setLoading(true);
		};
	}, [open]);

	return (
		<Dialog open={openDialog} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{`Télécharger la liste ${possibleCollection ? `"${getUserLabel(profile, possibleCollection.label)}"` : ""}`}</DialogTitle>
			<DialogContent>
				{loading ? (
					<MDBox display="flex" justifyContent="center" alignItems="center">
						<CircularProgress color="info" />
					</MDBox>
				) : (
					<MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center">
						<MDBox
							style={{
								width: "100%"
							}}
						>
							<InformationCard
								checkbox
								element={{
									title: "Télécharger la selection",
									description: `Télécharger les ${actualListLength} éléments à l'écran`
								}}
								onClick={() => setSelection("screen")}
								selected={selection === "screen"}
							/>
						</MDBox>
						<MDBox
							style={{
								width: "100%"
							}}
						>
							<InformationCard
								checkbox
								element={{
									title: "Télécharger toute la liste",
									description: `Télécharger les ${totalCount} éléments de la liste`
								}}
								onClick={() => setSelection("all")}
								selected={selection === "all"}
								disabled={totalCount === 0}
							/>
						</MDBox>
					</MDBox>
				)}
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={onClose}>
					Fermer
				</MDButton>
				<MDButton
					variant="contained"
					color="info"
					onClick={downloadSelection}
					disabled={!selection}
				>
					Télécharger
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default DownloadList;
